import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Tag } from 'antd';
import moment from 'moment';
import styled from 'styled-components';

import { formatMoneyCurrency } from 'core/utils/format';
import { ILmsAmountDue, ILmsBlockInvoice, NS } from 'core/utils/lmsSuspension';

interface IInvoicesProps {
  invoices: ILmsBlockInvoice[];
  /** Hide the due date (narrow table cells). */
  compact?: boolean;
}

/**
 * ТЗ 0074: the invoices a suspension is tied to — number (opens the
 * Fakturownia document in a new tab), amount still owed, days overdue, and a
 * «paid» mark for the ones already settled (live rows only; history snapshots
 * carry no paid-ness).
 */
export const LmsBlockInvoices = memo<IInvoicesProps>(
  ({ invoices, compact }) => {
    const { t } = useTranslation();
    if (!invoices.length) return <Muted>—</Muted>;
    return (
      <List>
        {invoices.map((inv, index) => (
          <Item key={inv.invoiceId || inv.number || index} $paid={!!inv.isPaid}>
            {inv.viewUrl ? (
              <a href={inv.viewUrl} target="_blank" rel="noopener noreferrer">
                {inv.number || '—'}
              </a>
            ) : (
              <span>{inv.number || '—'}</span>
            )}
            <Amount>{formatMoneyCurrency(inv.amount, inv.currency)}</Amount>
            {inv.isPaid ? (
              <Tag color="green">{t(`${NS}.invoice.paid`)}</Tag>
            ) : inv.daysOverdue > 0 ? (
              <Overdue>
                {t(`${NS}.invoice.daysOverdue`, { n: inv.daysOverdue })}
              </Overdue>
            ) : (
              <Muted>{t(`${NS}.invoice.notOverdue`)}</Muted>
            )}
            {!compact && inv.paymentTo ? (
              <Muted>
                {t(`${NS}.invoice.dueDate`, {
                  date: moment(inv.paymentTo).format('DD.MM.YYYY'),
                })}
              </Muted>
            ) : null}
          </Item>
        ))}
      </List>
    );
  },
);

interface IAmountsProps {
  amounts: ILmsAmountDue[];
}

/** Amounts per currency — never summed across currencies. */
export const LmsAmountsDue = memo<IAmountsProps>(({ amounts }) => {
  if (!amounts.length) return <Muted>—</Muted>;
  return (
    <List>
      {amounts.map((a) => (
        <Amount key={a.currency}>
          {formatMoneyCurrency(a.amount, a.currency)}
        </Amount>
      ))}
    </List>
  );
});

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const Item = styled.div<{ $paid: boolean }>`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  line-height: 1.5;
  opacity: ${({ $paid }) => ($paid ? 0.7 : 1)};

  .ant-tag {
    margin-right: 0;
    font-size: 11px;
    line-height: 16px;
  }
`;

const Amount = styled.span`
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`;

const Overdue = styled.span`
  color: #b91c1c;
  font-size: 12px;
  white-space: nowrap;
`;

const Muted = styled.span`
  color: #64748b;
  font-size: 12px;
`;
